import "./organization-discounts.scss";
import React, { useState, useEffect, useCallback } from "react";
import {
  GSListPage,
  GSInput,
  GSItemList,
  GSEmptyList,
} from "golfstatus_react_components";
import {
  faTimesCircle,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentOrganization } from "../../reducers/organizationSlice";

import {
  getOrganizationDiscountCodes,
  selectOrganizationDiscountCodes,
  selectLoading,
} from "../../reducers/discountSlice";

import { Outlet, useNavigate } from "react-router-dom";
import { useMatch } from "react-router-dom";
import OrganizationDiscountItem from "./organization-discount-item";

const OrganizationDiscounts = () => {
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const organizationDiscountCodes = useSelector(
    selectOrganizationDiscountCodes
  );
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const match = useMatch("/:id/discounts/*");
  const matchEdit = useMatch("/:id/discounts/:discountID/*");
  const loading = useSelector(selectLoading);

  const loadData = useCallback(() => {
    dispatch(getOrganizationDiscountCodes(match.params.id));
  }, [dispatch, match.params.id]);

  useEffect(() => {
    loadData();
  }, [loadData, currentOrganization]);

  const clearText = () => {
    setName("");
  };

  const searchChanged = (e) => {
    setName(e.target.value);
  };

  const add = () => {
    navigate("add");
  };

  const edit = (user) => {
    if (isSidePanelOpen()) {
      navigate(-1);
      return;
    }
    navigate(`${user.id}/edit`);
  };

  const getPageActions = () => {
    return [
      {
        buttonTitle: "Add Discount",
        type: "black mobile-icon",
        actionIcon: faPlus,
        actionClick: add,
      },
    ];
  };

  const isSidePanelOpen = () => {
    return (
      match.params["*"] === "add" ||
      matchEdit?.params["*"] === "edit" ||
      matchEdit?.params["*"] === "delete"
    );
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const getListPage = (title) => {
    const page = currentOrganization.allowDiscountCodes
      ? {
          title: title,
          getDefaultSearch: getDefaultSearch,
          getItemList: getItemList,
          getPageActions: getPageActions,
          isSidePanelOpen: isSidePanelOpen,
          getRouter: getRouter,
        }
      : {
          title: title,
          getDefaultSearch: getDefaultSearch,
          getItemList: getPremiumFeatureEmptyList,
          getRouter: getRouter,
        };
    return <GSListPage {...page} />;
  };

  const mailTo = () => {
    const recipient = "success@golfstatus.com";
    const subject = `Unlock Discount Codes for ${currentOrganization?.name}`;
    const body = `Hi,\n\nI’d like to learn more about unlocking the Discount Codes feature.\n\nThank you!\n\nOrganization: ${currentOrganization?.name}`;
    window.open(
      `mailto:${recipient}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`
    );
  };

  const getPremiumFeatureMessage = () => {
    return (
      <GSEmptyList
        title="Paid Feature"
        detail="Contact your sales or client success representative to unlock the Discounts feature."
        actions={[
          {
            title: "Send a Message",
            onClick: mailTo,
            type: "black",
          },
        ]}
      />
    );
  };

  const getPremiumFeatureEmptyList = () => {
    return (
      <GSItemList
        items={[]}
        type="vertical selectable"
        emptyMessage={getPremiumFeatureMessage()}
        loading={loading?.length !== 0}
        loadingMainText="Loading"
      ></GSItemList>
    );
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Add & Manage Discounts"
        detail="Discounts can be applied at checkout on all tournaments within this organization."
        actions={[
          {
            title: "Add Discount",
            buttonIcon: faPlus,
            onClick: add,
            type: "black",
          },
        ]}
      />
    );
  };

  const getEmptySearchMessage = () => {
    return (
      <GSEmptyList
        title="We couldn’t find the discount you’re looking for…"
        detail="Try changing the search term or add a new discount."
        actions={[
          {
            title: "Clear Search",
            onClick: clearText,
            type: "light-grey",
          },
          {
            title: "Add Discount",
            onClick: add,
            buttonIcon: faPlus,
            type: "black",
          },
        ]}
      />
    );
  };

  const getFilteredDiscounts = () => {
    return organizationDiscountCodes.filter?.((code) =>
      code.description?.toLowerCase?.().includes?.(name?.toLowerCase?.())
    );
  };

  const getItemList = () => {
    return (
      <GSItemList
        items={getFilteredDiscounts()}
        listItem={(discount) => <OrganizationDiscountItem {...discount} />}
        type="vertical selectable discounts"
        emptyMessage={name ? getEmptySearchMessage() : getEmptyListMessage()}
        itemSelected={edit}
        loading={loading?.length !== 0}
        loadingMainText="Loading"
      ></GSItemList>
    );
  };

  const getDefaultSearch = () => {
    return (
      <div className="search">
        <GSInput
          textValue={name}
          rightIconClick={clearText}
          rightIcon={faTimesCircle}
          onChange={searchChanged}
          placeholder="Search Discounts..."
          leftIcon={faSearch}
        ></GSInput>
      </div>
    );
  };

  return (
    <organization-discounts>{getListPage("Discounts")}</organization-discounts>
  );
};

export default OrganizationDiscounts;
